import { ref } from "vue";
import { defineStore } from "pinia";
import { useSignalR } from "@/composables/signalr";
import {
  type ChoreDto,
  type CategoryDto,
  type CreateCategoryDto,
  type CreateChoreDto,
  type UpdateChoreDto,
  type DifficultyLevel,
  getApiV1Categories,
  getApiV1Chores,
  postApiV1Categories,
  deleteApiV1CategoriesById,
  postApiV1Chores,
  putApiV1ChoresById,
  deleteApiV1ChoresById,
} from "@/client";
import type {
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
} from "@/types/signalr-events";

export const useChoreSettingsStore = defineStore("choreSettings", () => {
  const settingsDataLoading = ref(false);
  const settingsDataLoaded = ref(false);
  const categories = ref<CategoryDto[]>([]);
  const choreTemplates = ref<ChoreDto[]>([]);
  const { subscribe, onReconnected } = useSignalR();
  // Live events sent while offline are lost; reload what this store already showed.
  onReconnected(() => {
    if (settingsDataLoaded.value) {
      fetchSettingsData().catch((err) => console.error("Resync after reconnect failed", err));
    }
  });

  async function fetchSettingsData() {
    settingsDataLoading.value = true;
    try {
      const categoriesResponse = await getApiV1Categories({
        security: [
          {
            scheme: "bearer",
            type: "http",
          },
        ],
      });
      if (categoriesResponse.error) {
        console.error("Error fetching categories:", categoriesResponse.error);
      } else {
        categories.value = categoriesResponse.data!;
      }
      const choresResponse = await getApiV1Chores();
      if (choresResponse.error) {
        console.error("Error fetching chores:", choresResponse.error);
      } else {
        choreTemplates.value = choresResponse.data!;
      }
      settingsDataLoaded.value = true;
    } finally {
      settingsDataLoading.value = false;
    }
  }

  async function createCategory(category: CreateCategoryDto) {
    const newCategoryResponse = await postApiV1Categories({
      body: category,
    });
    if (newCategoryResponse.error) {
      console.error("Error creating category:", newCategoryResponse.error);
      throw new Error("Failed to create category");
    } else {
      return newCategoryResponse.data!;
    }
  }

  async function deleteCategory(id: string) {
    const response = await deleteApiV1CategoriesById({ path: { id } });
    if (response.error) {
      console.error("Error deleting category:", response.error);
      throw new Error("Failed to delete category");
    }
  }

  async function ensureChoreSettingsDataLoaded() {
    if (!settingsDataLoaded.value) {
      await fetchSettingsData();
    }
  }

  async function createChore(chore: CreateChoreDto) {
    const response = await postApiV1Chores({ body: chore });
    if (response.error) {
      console.error("Error creating chore:", response.error);
      throw new Error("Failed to create chore");
    }
    const newChore = response.data!;
    return newChore;
  }

  async function updateChore(choreId: string, chore: UpdateChoreDto) {
    const response = await putApiV1ChoresById({
      path: { id: choreId },
      body: chore,
    });
    if (response.error) {
      console.error("Error updating chore:", response.error);
      throw new Error("Failed to update chore");
    }
  }

  async function deleteChore(id: string) {
    const response = await deleteApiV1ChoresById({ path: { id } });
    if (response.error) {
      console.error("Error deleting chore:", response.error);
      throw new Error("Failed to delete chore");
    }
  }

  //#region SignalR
  subscribe<EntityUpdatedEvent<CategoryDto>>(
    "CategoryUpdated",
    async (event) => {
      console.debug("CategoryUpdated event received:", event);
      createOrUpdateCategory(event.entity);
    },
  );
  subscribe<EntityDeletedEvent>("CategoryDeleted", async (event) => {
    console.debug("CategoryDeleted event received:", event);
    await fetchSettingsData();
  });
  subscribe<EntityCreatedEvent<CategoryDto>>(
    "CategoryCreated",
    async (event) => {
      console.debug("CategoryCreated event received:", event);
      createOrUpdateCategory(event.entity);
    },
  );

  function createOrUpdateCategory(category: CategoryDto) {
    const index = categories.value.findIndex((c) => c.id === category.id);
    if (index === -1) {
      categories.value.push(category);
    } else {
      categories.value[index] = category;
    }
  }

  //#region SignalR
  subscribe<EntityUpdatedEvent<ChoreDto>>("ChoreUpdated", async (event) => {
    console.debug("ChoreUpdated event received:", event);
    if (event.entity.isDeleted) {
      const index = choreTemplates.value.findIndex(
        (chore) => chore.id === event.entity.id,
      );
      if (index !== -1) {
        choreTemplates.value.splice(index, 1);
      }
    } else {
      createOrUpdateChore(event.entity);
    }
  });
  subscribe<EntityDeletedEvent>("ChoreDeleted", async (event) => {
    console.debug("ChoreDeleted event received:", event);
    const index = choreTemplates.value.findIndex(
      (chore) => chore.id === event.entityId,
    );
    if (index !== -1) {
      choreTemplates.value.splice(index, 1);
    }
  });
  subscribe<EntityCreatedEvent<ChoreDto>>("ChoreCreated", async (event) => {
    console.debug("ChoreCreated event received:", event);
    createOrUpdateChore(event.entity);
  });

  function createOrUpdateChore(chore: ChoreDto) {
    const index = choreTemplates.value.findIndex((c) => c.id === chore.id);
    if (index === -1) {
      choreTemplates.value.push(chore);
    } else {
      choreTemplates.value[index] = chore;
    }
  }
  //#endregion

  return {
    categories,
    choreTemplates,
    choreSettingsDataLoading: settingsDataLoading,
    choreSettingsDataLoaded: settingsDataLoaded,

    fetchChoreSettingsData: fetchSettingsData,
    createCategory,
    deleteCategory,
    ensureChoreSettingsDataLoaded,
    createChoreTemplate: createChore,
    updateChoreTemplate: updateChore,
    deleteChoreTemplate: deleteChore,
  };
});
